import { PureComponent, ReactNode } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Table, Input } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../../../store/reducers';
import { Actions } from '../../../store/actions';
import { Procedure } from '../../../store/types/prescription';
import fetchMatchingProcedures, {
    ProcedureSuggestions,
} from '../../../api/fetchMatchingProcedures';
import { EkaFocosableElement } from '../../common/types/EkaFocusable';
import { track } from '../../../tracking';
import { LOCAL_ID_PREFIX } from '../../../constants';
import { generateLocalIdForEntity, randomString } from '../../../store/utils';
import PadGenieAutoSuggestFuseManager, { PROCEDURES } from '../PadGenieAutoSuggestFuseManager';
import { buildOptions } from '../rightPanel/workspace/padV2/SymDiaTableV2';
import { columnHeaderContent, headerStyle } from '../../common/TableUtils';
import { ProcedureIcon } from '../../common/Icons';
import TableWithDragableRows, {
    DragHandle,
} from '../rightPanel/workspace/padV2/TableWithDragableRows';
import { EditableCell, EditableCellV2 } from '../rightPanel/workspace/padV2/MedTableRenderer';
import { PROCEDURE_AS_INPUT, toPxProcedureFromApiObj } from './ProcedureAutoSuggest';
import { getElementsFromPrescription } from '../../../store/selectors/prescriptionPad';
import {
    copyKeyToAll,
    deleteElement,
    swapPxElement,
    upsertElement,
} from '../../../store/actions/prescriptionPad';
import { PAD_V2_CONTAINER } from '../rightPanel/workspace/PadContainer';
import LabTestsDateSelector, {
    LAB_TESTS_FOLLOWUP_TYPE,
} from '../rightPanel/workspace/padV2/LabTestsDateSelector';
import { labTestOnDefaultOption } from './DentalChart/DentalProcedure/DentalProcedureTable';
import CopyToAll from '../rightPanel/workspace/padV2/CopyToAll';
const { Column } = Table;

type Props = {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    elements: ReadonlyArray<Procedure>;
    containerId: string;
    onElementSelect: (index: number, option: ProcedureSuggestions | Procedure) => void;
    onElementUpdate: (index: number, option: Procedure) => void;
    onElementDelete: (index: number) => void;
    onDrag: ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => void;
    onCopyDate: (index: number) => void;
};

class ProcedureTable extends PureComponent<Props> {
    static REF_MAP: EkaFocosableElement[][] = [];

    TABLE_ID = 'procedure-table';

    getStrValueAtIndex = (element: Procedure, index: number): string | undefined => {
        switch (index) {
            case 0:
                return element.id ? element.name : undefined;
            case 1:
                return element.notes;
        }
        return '';
    };

    findIndexForRecordKey = (key: string): number => {
        return this.props.elements.findIndex((e, index) => this.rowKeyFromId(e.id, index) === key);
    };

    elementSuggestApi = async (text: string): Promise<ProcedureSuggestions[]> => {
        try {
            const res = await fetchMatchingProcedures(text);
            return Array.isArray(res) ? res : [];
        } catch (e) {
            track('ERROR', {
                on: 'ProcedureTable#elementSuggestApi',
                error: (e as Error).message,
            });
            return [];
        }
    };

    createOption = (value: string) => {
        const id = `${LOCAL_ID_PREFIX}dp-${new Date().getTime()}-${randomString(4)}`;
        return {
            id: id,
            name: value.trim(),
        };
    };

    handleSearch = async (value: string): Promise<ProcedureSuggestions[]> => {
        const { elements } = this.props;
        const fuse = PadGenieAutoSuggestFuseManager.getFuseForType(PROCEDURES);
        const gElements = PadGenieAutoSuggestFuseManager.getOriginalListForType(PROCEDURES);

        const all = await buildOptions<ProcedureSuggestions>(
            value,
            elements,
            gElements || [],
            this.elementSuggestApi,
            fuse,
            this.createOption,
            true,
        );

        return all;
    };

    nameHeaderContent = columnHeaderContent('Procedure');
    dateHeaderContent = columnHeaderContent('Date');
    notesHeaderContent = columnHeaderContent('Notes');

    OptionIcon = ProcedureIcon('w-14 h-12 fill-darwin-accent-medicines-pink-800');

    renderCellAutosuggestItem = (option: Procedure) => {
        return (
            <RenderCellAutosuggestItemJsx
                option={option}
                wrapperClassName={'mx-dropdown-item'}
                icon={this.OptionIcon}
                iconWrapperClassName={'circle-mx'}
            />
        );
    };

    setPropertyInputRef = (ref: EkaFocosableElement, elIndex: number, propertyIndex: number) => {
        if (!ProcedureTableComponent.REF_MAP[elIndex]) {
            ProcedureTableComponent.REF_MAP[elIndex] = [];
        }

        ProcedureTableComponent.REF_MAP[elIndex][propertyIndex] = ref;
        return;
    };

    renderName = (_: Procedure, __: Procedure, index: number) => {
        const { elements, containerId } = this.props;

        return {
            children: (
                <div className="flex space-x-16 items-center">
                    <DragHandle />
                    <div className="font-700 w-full text-neutral-black text-14 placeholder-neutral-6">
                        <EditableCell
                            fieldName={`dp-${index}`}
                            label={'Procedure'}
                            setRef={(ref) => {
                                this.setPropertyInputRef(ref, index, 0);
                            }}
                            debounceDelay={200}
                            customDropdownClass={`py-8 mx-dropdown rounded-8`}
                            customDropdownWidth={350}
                            optionsRenderer={this.renderCellAutosuggestItem}
                            onSearch={this.handleSearch}
                            getLabelToCompare={(o) => o.name}
                            selectedValue={
                                elements[index] && elements[index].id ? elements[index] : undefined
                            }
                            onSelect={(option) => {
                                option && this.props.onElementSelect(index, option);
                                setTimeout(() => {
                                    const t = document.getElementById(
                                        PROCEDURE_AS_INPUT,
                                    ) as HTMLInputElement;
                                    t && t.focus();
                                }, 500);
                            }}
                            containerId={containerId}
                            placeholder={`Type procedure`}
                        />
                    </div>
                </div>
            ),
        };
    };

    renderDate = (_: Procedure, el: Procedure, index: number) => {
        return {
            children: (
                <div>
                    <LabTestsDateSelector
                        defaultOptions={labTestOnDefaultOption}
                        date={el.date}
                        upsertElement={(procedure) => {
                            this.props.onElementUpdate(index, procedure);
                        }}
                        elements={this.props.elements as Procedure[]}
                        el={{ ...el, medIndex: index }}
                        containerId={this.props.containerId}
                        type={LAB_TESTS_FOLLOWUP_TYPE.DATE}
                    />

                    <CopyToAll
                        id={this.copyDateId(index)}
                        onClick={() => this.props.onCopyDate(index)}
                        tableId={this.TABLE_ID}
                    />
                </div>
            ),
        };
    };

    renderNotes = (_: Procedure, el: Procedure, index: number) => {
        const value = el.notes || '';

        return {
            children: (
                <div className="flex space-x-8 items-center">
                    <EditableCellV2
                        defaultValue={value}
                        onBlur={(e) => {
                            this.props.onElementUpdate(index, { ...el, notes: e.target.value });
                        }}
                        setRef={(ref) => {
                            this.setPropertyInputRef(ref, index, 5);
                            return;
                        }}
                        label="Add notes here"
                    >
                        <Input.TextArea
                            autoFocus
                            defaultValue={value}
                            rows={1}
                            placeholder="Add notes here"
                        />
                    </EditableCellV2>
                </div>
            ),
        };
    };

    renderDelete = (_: Procedure, __: Procedure, index: number) => {
        return {
            children: (
                <div className="flex space-x-8 items-center justify-center">
                    <DeleteOutlined
                        style={{ color: '#616A8F' }}
                        size={24}
                        onClick={() => this.props.onElementDelete(index)}
                        onPointerLeaveCapture={undefined}
                    />
                </div>
            ),
        };
    };

    rowKeyFromId = (id: string, index?: number): string => `${'procedure'}-${id}-${index}`;

    copyDateId = (index: number): string => `copy-date-v2-svg-${index}`;

    render(): ReactNode {
        const { elements, onDrag } = this.props;
        if (elements.length === 0) {
            return null;
        }
        return (
            <TableWithDragableRows
                id={this.TABLE_ID}
                showHeader
                tableLayout="fixed"
                className="procedure-table"
                rowClassName={() => 'editable-row'}
                bordered
                dataSource={elements}
                pagination={false}
                rowKey={(record, index) => this.rowKeyFromId(record.id, index)}
                findIndexForRecordKey={this.findIndexForRecordKey}
                onSortEnd={onDrag}
                scroll={{
                    x: 600,
                }}
            >
                <Column
                    title={this.nameHeaderContent}
                    onHeaderCell={headerStyle}
                    key="procedure-row-name"
                    className="procedure-row-name"
                    render={this.renderName}
                    fixed={true}
                />
                <Column
                    title={this.dateHeaderContent}
                    onHeaderCell={headerStyle}
                    key="procedure-row-date"
                    className="procedure-row-date"
                    render={this.renderDate}
                />
                <Column
                    title={this.notesHeaderContent}
                    onHeaderCell={headerStyle}
                    key="procedure-row-notes"
                    className="procedure-row-notes"
                    render={this.renderNotes}
                />
                <Column
                    onHeaderCell={headerStyle}
                    key="procedure-row-delete"
                    className={`p-5 procedure-row-delete`}
                    fixed="right"
                    render={this.renderDelete}
                />
            </TableWithDragableRows>
        );
    }
}

export const ProcedureTableComponent = connect()(ProcedureTable);

const PadV2ProcedureTable: React.FC = () => {
    const dispatch = useDispatch();
    const elements = useSelector((state: GlobalState) =>
        getElementsFromPrescription<Procedure>('procedures')(state),
    );

    const onElementSelect = (index: number, option: ProcedureSuggestions) => {
        const data = option || { id: '', name: '' };
        dispatch(upsertElement('procedures', toPxProcedureFromApiObj(data), index));
    };

    const onElementUpdate = (index: number, option: Procedure) => {
        dispatch(upsertElement('procedures', option, index));
    };

    const onElementDelete = (index: number) => {
        dispatch(deleteElement('procedures', index));
    };

    const onDrag = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
        if (oldIndex !== newIndex) {
            dispatch(swapPxElement('procedures', oldIndex, newIndex));
        }
    };

    const onCopyDate = (index?: number) => {
        if (index === undefined) {
            return;
        }
        const element = elements[index];

        if (!element.date) {
            return;
        }

        dispatch(copyKeyToAll('procedures', 'date', element.date));
    };

    return (
        <ProcedureTableComponent
            onElementUpdate={onElementUpdate}
            onElementDelete={onElementDelete}
            onElementSelect={onElementSelect}
            elements={elements}
            containerId={PAD_V2_CONTAINER}
            onDrag={onDrag}
            onCopyDate={onCopyDate}
        />
    );
};

export default PadV2ProcedureTable;

export const RenderCellAutosuggestItemJsx: React.FC<{
    option: { name: string };
    wrapperClassName?: string;
    iconWrapperClassName?: string;
    icon: JSX.Element | string;
}> = ({ option, wrapperClassName, icon, iconWrapperClassName }) => {
    return (
        <div
            className={`flex items-center justify-between ${wrapperClassName} cursor-pointer font-lato w-full`}
        >
            <div className="flex items-center space-x-8">
                <div className={iconWrapperClassName}>
                    <p>{icon}</p>
                </div>
                <p className="text-neutral-1 text-14 w-full">{option.name}</p>
            </div>
        </div>
    );
};
